import React, { useEffect, useRef, useState } from 'react';
import './Profile.css';
import { useAuth } from '../AuthContext';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';

const CLOUDINARY_UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;
const CLOUDINARY_CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;

const Profile = () => {
  const { user, refreshUserData } = useAuth();

  const [displayName, setDisplayName] = useState('');
  const [originalDisplayName, setOriginalDisplayName] = useState('');
  const [profilePic, setProfilePic] = useState('');
  const [tempImage, setTempImage] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [savingImage, setSavingImage] = useState(false);
  const [savingName, setSavingName] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [nameError, setNameError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const fileInputRef = useRef();

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) return;
      
      try {
        const userRef = doc(db, 'users', user.uid);
        const snap = await getDoc(userRef);

        if (snap.exists()) {
          const data = snap.data();
          const name = data.displayName || user.displayName || '';
          setDisplayName(name);
          setOriginalDisplayName(name);
          setProfilePic(data.photoURL || user.photoURL || '');
        } else {
          // Create initial user document
          const initialData = {
            displayName: user.displayName || '',
            photoURL: user.photoURL || '',
            email: user.email,
            createdAt: new Date()
          };
          await setDoc(userRef, initialData);
          setDisplayName(initialData.displayName);
          setOriginalDisplayName(initialData.displayName);
          setProfilePic(initialData.photoURL);
        }
      } catch (error) {
        
      }
    };
    
    fetchProfile();
  }, [user]);

  const clearMessages = () => {
    setUploadError('');
    setSuccessMessage('');
    setNameError('');
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    clearMessages();

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setUploadError('Please select a valid image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('Image size must be less than 5MB');
      return;
    }

    try {
      setUploadingImage(true);
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
      formData.append('folder', 'profile_pics');

      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
        {
          method: 'POST',
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.secure_url) {
        setTempImage(data.secure_url);
      } else {
        throw new Error('No secure URL returned from Cloudinary');
      }
    } catch (error) {
   
      setUploadError('Failed to upload image. Please try again.');
    } finally {
      setUploadingImage(false);
    }
  };

  const saveImageToFirestore = async () => {
    if (!tempImage || !user) return;

    try {
      setSavingImage(true);
      clearMessages();
      
      const userRef = doc(db, 'users', user.uid);
      
      await updateDoc(userRef, { 
        photoURL: tempImage,
        updatedAt: new Date()
      });
      
      setProfilePic(tempImage);
      setTempImage('');
      setSuccessMessage('Profile image updated successfully!');
      
      // Refresh user data in AuthContext to update header
      if (refreshUserData) {
        await refreshUserData();
      }
      
      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      
      setUploadError('Failed to save image. Please try again.');
    } finally {
      setSavingImage(false);
    }
  };

  const saveDisplayName = async () => {
    if (!user) return;

    // Validate display name
    const trimmedName = displayName.trim();
    
    if (!trimmedName) {
      setNameError('Display name cannot be empty');
      return;
    }

    if (trimmedName.length < 2) {
      setNameError('Display name must be at least 2 characters');
      return;
    }

    if (trimmedName.length > 50) {
      setNameError('Display name must be less than 50 characters');
      return;
    }

    // Check if name actually changed
    if (trimmedName === originalDisplayName) {
      setNameError('No changes to save');
      return;
    }

    try {
      setSavingName(true);
      clearMessages();
      
      const userRef = doc(db, 'users', user.uid);
      
      await updateDoc(userRef, { 
        displayName: trimmedName,
        updatedAt: new Date()
      });
      
      setOriginalDisplayName(trimmedName);
      setSuccessMessage('Display name updated successfully!');
      
      // Refresh user data in AuthContext
      if (refreshUserData) {
        await refreshUserData();
      }
    } catch (error) {
      
      setNameError('Failed to save display name. Please try again.');
    } finally {
      setSavingName(false);
    }
  };

  const cancelImageChange = () => {
    setTempImage('');
    clearMessages();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const cancelNameChange = () => {
    setDisplayName(originalDisplayName);
    setNameError('');
  };

  const getCurrentImage = () => {
    return tempImage || profilePic || 'https://via.placeholder.com/150x150/e5e7eb/6b7280?text=No+Image';
  };

  const hasNameChanged = displayName.trim() !== originalDisplayName;

  if (!user) {
    return null;
  }

  return (
    <div className="profile-container">
      <div className="profile-header">
        <h1>Profile Settings</h1>
        <p>Manage your account information and preferences</p>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="success-message">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          {successMessage}
        </div>
      )}
      
      {/* Profile Picture Section */}
      <div className="profile-card">
        <div className="card-header">
          <h2>Profile Picture</h2>
          <p>Update your profile picture</p>
        </div>
        
        <div className="profile-pic-section">
          <div className="profile-pic-wrapper">
            <div className="profile-pic-container">
              <img
                src={getCurrentImage()}
                alt="Profile"
                className="profile-pic"
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/150x150/e5e7eb/6b7280?text=Error';
                }}
              />
              {tempImage && <div className="image-preview-badge">Preview</div>}
            </div>
            
            <div className="profile-pic-actions">
              <button 
                onClick={() => fileInputRef.current?.click()} 
                className="btn btn-secondary"
                disabled={uploadingImage || savingImage}
              >
                {uploadingImage ? (
                  <>
                    <div className="btn-spinner"></div>
                    Uploading...
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/>
                      <circle cx="12" cy="13" r="3"/>
                    </svg>
                    Change Photo
                  </>
                )}
              </button>
              
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleImageUpload}
              />
            </div>
            
            {tempImage && (
              <div className="temp-image-actions">
                <button 
                  className="btn btn-primary" 
                  onClick={saveImageToFirestore} 
                  disabled={savingImage}
                >
                  {savingImage ? (
                    <>
                      <div className="btn-spinner"></div>
                      Saving...
                    </>
                  ) : (
                    'Save Image'
                  )}
                </button>
                <button 
                  className="btn btn-outline" 
                  onClick={cancelImageChange}
                  disabled={savingImage}
                >
                  Cancel
                </button>
              </div>
            )}
            
            {uploadError && (
              <div className="error-message">
                <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {uploadError}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Personal Information Section */}
      <div className="profile-card">
        <div className="card-header">
          <h2>Personal Information</h2>
          <p>Your account details</p>
        </div>
        
        <div className="form-grid">
          <div className="form-group">
            <label htmlFor="displayName">Display Name</label>
            <input
              id="displayName"
              type="text"
              value={displayName}
              onChange={(e) => {
                setDisplayName(e.target.value);
                setNameError('');
              }}
              placeholder="Enter your display name"
              maxLength={50}
            />
            <div className="field-hint">
              This is the name that will be displayed across the platform
            </div>
            
            {nameError && (
              <div className="error-message" style={{ marginTop: '8px' }}>
                <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {nameError}
              </div>
            )}
            
            {hasNameChanged && !nameError && (
              <div className="temp-image-actions" style={{ marginTop: '12px' }}>
                <button 
                  className="btn btn-primary" 
                  onClick={saveDisplayName} 
                  disabled={savingName}
                >
                  {savingName ? (
                    <>
                      <div className="btn-spinner"></div>
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </button>
                <button 
                  className="btn btn-outline" 
                  onClick={cancelNameChange}
                  disabled={savingName}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
          
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input 
              id="email"
              type="email" 
              value={user.email} 
              disabled 
              className="disabled"
            />
            <div className="field-hint">
              Your email address cannot be changed
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;